import { z } from "zod";
import { SCOPES } from "../constants/scopes.js";
/**
 * Personal Access Token (PAT) schemas — SSOT for the `/api/tokens`
 * surface and the `/settings/tokens` UI.
 *
 * Request schemas are tight (validate user input); response schemas are
 * deliberately loose on enum-shaped fields (`scopes`) so that a token
 * issued under an older/newer scope vocabulary never breaks the whole
 * list render — a single non-conforming row should degrade gracefully,
 * not 500 the page. See feedback: response schemas loose, request tight.
 *
 * Canon: docs/enhancements/ideation-capture-2026-05.md §A.5,
 *        docs/design-canon/04-shared/ssot.md.
 */
// ─── Scope ─────────────────────────────────────────────────
/** Tight: only currently-defined scopes may be requested at creation. */
export const ScopeSchema = z.enum(SCOPES);
// ─── Create (request) ──────────────────────────────────────
export const TokenCreateSchema = z.object({
    label: z.string().min(1).max(60),
    scopes: z.array(ScopeSchema).min(1).max(SCOPES.length),
    // null = never expires. Caller sends an ISO-8601 instant or null.
    expiresAt: z.string().datetime().nullable().default(null),
});
// ─── Token (response, no secret) ───────────────────────────
/**
 * A token as surfaced in the list / after revoke. Never carries the
 * secret. `prefix` is the 16-char public lookup id, also the displayable
 * identifier in the UI (the secret half is shown exactly once, at create).
 */
export const PersonalAccessTokenSchema = z.object({
    id: z.string().uuid(),
    label: z.string(),
    prefix: z.string(),
    // Loose on purpose (see file header): tolerate scopes outside the
    // current enum so the list never breaks on a drifted row.
    scopes: z.array(z.string()),
    lastUsedAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime().nullable(),
    revokedAt: z.string().datetime().nullable(),
    createdAt: z.string().datetime(),
});
export const TokenListResponseSchema = z.object({
    tokens: z.array(PersonalAccessTokenSchema),
});
// ─── Create (response — secret shown ONCE) ─────────────────
/**
 * Returned only from `POST /api/tokens`. `token` is the full plaintext
 * (`l30_pat_<prefix>_<secret>`) and is never retrievable again — the
 * server stores only the SHA-256 of the secret half.
 */
export const TokenCreateResponseSchema = PersonalAccessTokenSchema.extend({
    token: z.string(),
});
// ─── Connector document-write grant (ADR-0104) ─────────────
/**
 * The OAuth-connector equivalent of minting a PAT with `files:write`.
 * An OAuth access token cannot carry that scope (Clerk's dynamically
 * registered clients are limited to OIDC scopes), so the standing consent
 * is recorded server-side as an explicit, expiring, revocable grant.
 *
 * `days` is bounded rather than optional-forever: a grant that never
 * lapses is the thing we are trying not to create.
 */
export const CONNECTOR_GRANT_MAX_DAYS = 365;
export const ConnectorWriteGrantCreateSchema = z.object({
    days: z.number().int().min(1).max(CONNECTOR_GRANT_MAX_DAYS).default(90),
});
/** LOOSE response (see file header): timestamps plain strings. */
export const ConnectorWriteGrantSchema = z.object({
    active: z.boolean(),
    grantedAt: z.string().nullable(),
    expiresAt: z.string().nullable(),
    lastUsedAt: z.string().nullable(),
});
//# sourceMappingURL=tokens.js.map